import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { ModalService } from '@bling-fe/shared/modals/base-modal';
import { marker as _ } from '@bling-fe/shared/utils';
import { PdfModalComponent } from '../../modals/pdf-modal/pdf-modal.component';
import { DocumentRepository } from '../repositories/document.repository';
import { NotificationService } from './notification.service';

/**
 * Opens a document the way a reader expects from a link click: PDFs in the
 * in-app preview modal, everything else as an immediate download — no
 * intermediate page. Inaccessible/missing documents fall through to the
 * /documents/:id page, which renders the friendly error state.
 */
@Injectable({ providedIn: 'root' })
export class DocumentOpenerService {
  constructor(
    private documentRepository: DocumentRepository,
    private modalService: ModalService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  openById(id: number): void {
    this.documentRepository
      .findById(id)
      .pipe(take(1))
      .subscribe({
        next: document => this.openResolved(document),
        error: () => this.router.navigate(['/documents', id])
      });
  }

  openResolved(document: {
    url: string;
    title: string;
    content_type: string;
  }): void {
    if (!document.url) {
      this.notificationService.error(_('COMPONENTS.MEDIA.UNAVAILABLE'));
      return;
    }

    if (document.content_type === 'application/pdf') {
      this.modalService.presentAndGetResult(PdfModalComponent, {
        pdfUrl: document.url,
        title: document.title
      });
      return;
    }

    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: document.url });
      return;
    }

    // Within the click's transient activation this opens a tab; if the
    // activation has expired the popup is blocked and we navigate instead.
    const tab = window.open(document.url, '_blank');
    if (!tab) {
      window.location.assign(document.url);
    }
  }
}
